import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pill } from '../ui';
import { useEngineerVisits } from '../engineers/useEngineerVisits';
import { EngineerVisitsPayload } from '../engineers/engineerVisitTypes';
import { formatVisitDate, taskLabels } from '../engineers/engineerVisitUtils';
import { formatAgo } from '../cleaners/cleanerAttendanceUtils';

const RECENT_VISITS = 3;

type Props = {
  locationId: number | null;
};

function buildTooltip(data: EngineerVisitsPayload): string {
  const recent = data.visits.map((v) => {
    const did = [...taskLabels(v.tasks, data.taskOptions), ...(v.notes ? [v.notes] : [])];
    return [`${formatVisitDate(v.visitedAt)} · ${v.engineerName}`, ...did.map((d) => `  - ${d}`)].join(
      '\n'
    );
  });
  return recent.length
    ? ['Recent:', ...recent, '', 'Click for engineer visits'].join('\n')
    : 'No engineer visits logged yet. Click to log one';
}

const EngineerCard: React.FC<{ locationId: number }> = ({ locationId }) => {
  const navigate = useNavigate();
  const { data, error } = useEngineerVisits({ locationId, limit: RECENT_VISITS });

  if (!data) {
    if (!error) return null;
    return (
      <div className="sq-context-card" title={error}>
        <div className="sq-context-card-top">
          <span className="sq-context-card-name">Engineer</span>
          <Pill>Unavailable</Pill>
        </div>
      </div>
    );
  }

  const last = data.visits[0];

  return (
    <button
      type="button"
      className="sq-context-card is-link"
      title={buildTooltip(data)}
      onClick={() => navigate(`/maintenance/engineers?locationId=${locationId}`)}
    >
      <span className="sq-context-card-top">
        <span className="sq-context-card-name">Engineer</span>
        <Pill tone={last ? 'info' : 'neutral'}>{last ? formatAgo(last.visitedAt) : 'No visits'}</Pill>
      </span>
      <span className="sq-muted">{last ? `Last: ${last.engineerName}` : 'No visits logged'}</span>
    </button>
  );
};

const StatusTimelineEngineerCard: React.FC<Props> = ({ locationId }) => {
  if (locationId == null) return null;
  return <EngineerCard locationId={locationId} />;
};

export default StatusTimelineEngineerCard;
